import { useSyncExternalStore } from 'react';
import {
  XMAS_DECOR_KEY,
  XMAS_SNOW_KEY,
  christmasOn,
  inChristmasSeason,
  prefIsOn,
  snowOn,
} from '../../lib/christmas';

/**
 * Reads and writes the two Christmas switches, and tells every component that
 * shows them (the shell, Settings) to re-read when one changes. Storage can be
 * unavailable (private mode); then the switch still works for this session
 * because the change event fires regardless, and the default is on.
 */
const CHANGE_EVENT = 'tap-xmas-change';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

export function setChristmasPref(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? 'on' : 'off');
  } catch {
    // Remembering failed; the event below still updates this session.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** The raw switch (on unless turned off), for Settings. */
export function useChristmasPref(key: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => prefIsOn(read(key)),
    () => true,
  );
}

function useReducedMotion(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window.matchMedia !== 'function') return () => undefined;
      const query = window.matchMedia(REDUCED_MOTION);
      query.addEventListener('change', onChange);
      return () => {
        query.removeEventListener('change', onChange);
      };
    },
    () => typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches,
    () => false,
  );
}

/** Whether the parols and scene show right now: in season and not switched off. */
export function useDecorOn(): boolean {
  const pref = useChristmasPref(XMAS_DECOR_KEY);
  return christmasOn(inChristmasSeason(new Date()), pref ? null : 'off');
}

/** Whether the snow falls right now. */
export function useSnowOn(): boolean {
  const pref = useChristmasPref(XMAS_SNOW_KEY);
  const reduced = useReducedMotion();
  return snowOn(inChristmasSeason(new Date()), pref ? null : 'off', reduced);
}

/** For Settings: is it the season, so the switches mean something right now. */
export function useInSeason(): boolean {
  return inChristmasSeason(new Date());
}
